import { TimeRange } from '@mi-front/core';
import { create } from 'zustand';

export interface StreamSelection {
  readonly tsA: number | null;
  readonly tsB: number | null;
}

interface SelectionState {
  readonly selectionByStationId: Record<string, StreamSelection>;

  // Actions
  setSelection: (stationId: string, tsA: number | null, tsB: number | null) => void;
  setSelectionStart: (stationId: string, ts: number | null) => void;
  setSelectionEnd: (stationId: string, ts: number | null) => void;
  clearSelection: (stationId: string) => void;

  // Getters
  getSelection: (stationId: string) => StreamSelection;
  selectionExists: (stationId: string) => boolean;
  getSelectionRange: (stationId: string) => TimeRange<number>;
  canStartSelection: (stationId: string, ts: number) => boolean;
  canEndSelection: (stationId: string, ts: number) => boolean;
}

/**
 * Hook to get setSelection action.
 */
export function useSetSelectionAction() {
  return useSelectionStore((state) => state.setSelection);
}

/**
 * Hook to get setSelectionStart action.
 */
export function useSetSelectionStartAction() {
  return useSelectionStore((state) => state.setSelectionStart);
}

/**
 * Hook to get setSelectionEnd action.
 */
export function useSetSelectionEndAction() {
  return useSelectionStore((state) => state.setSelectionEnd);
}

/**
 * Hook to get clearSelection action.
 */
export function useClearSelectionAction() {
  return useSelectionStore((state) => state.clearSelection);
}

const emptySelection: StreamSelection = { tsA: null, tsB: null };

export const useSelectionStore = create<SelectionState>()((set, get) => ({
  selectionByStationId: {},

  setSelection: (stationId, tsA, tsB) => {
    set((state) => ({
      selectionByStationId: {
        ...state.selectionByStationId,
        [stationId]: { tsA, tsB },
      },
    }));
  },

  setSelectionStart: (stationId, ts) => {
    set((state) => ({
      selectionByStationId: {
        ...state.selectionByStationId,
        [stationId]: {
          ...(state.selectionByStationId[stationId] ?? emptySelection),
          tsA: ts,
        },
      },
    }));
  },

  setSelectionEnd: (stationId, ts) => {
    set((state) => ({
      selectionByStationId: {
        ...state.selectionByStationId,
        [stationId]: {
          ...(state.selectionByStationId[stationId] ?? emptySelection),
          tsB: ts,
        },
      },
    }));
  },

  clearSelection: (stationId) => {
    set((state) => ({
      selectionByStationId: {
        ...state.selectionByStationId,
        [stationId]: emptySelection,
      },
    }));
  },

  getSelection: (stationId) => {
    return get().selectionByStationId[stationId] ?? emptySelection;
  },

  selectionExists: (stationId) => {
    const { tsA, tsB } = get().selectionByStationId[stationId] ?? emptySelection;
    return !!(tsA && tsB);
  },

  getSelectionRange: (stationId) => {
    const { tsA, tsB } = get().selectionByStationId[stationId] ?? emptySelection;

    return {
      from: Math.min(tsA ?? Infinity, tsB ?? Infinity),
      to: Math.max(tsA ?? 0, tsB ?? 0),
    };
  },

  canStartSelection: (stationId, ts) => {
    const { tsA, tsB } = get().selectionByStationId[stationId] ?? emptySelection;

    // Allow to start selection when:
    // - there is no tsA and tsB
    // - the boundary tsA is only set
    // - the boundary tsB is only set, and the cursor lies before it
    // - cursor lies before the maximum of the current boundaries
    return Boolean(
      (!tsA && !tsB) ||
      (tsA && !tsB) ||
      (!tsA && tsB && tsB > ts) ||
      (tsA && tsB && ts < Math.max(tsA, tsB)),
    );
  },

  canEndSelection: (stationId, ts) => {
    const { tsA, tsB } = get().selectionByStationId[stationId] ?? emptySelection;

    // Allow to end selection when:
    // - there is no tsA and tsB
    // - the boundary tsB is only set
    // - the boundary tsA is only set, and the cursor lies after it
    // - cursor lies after the minimum of the current boundaries
    return Boolean(
      (!tsA && !tsB) ||
      (tsB && !tsA) ||
      (!tsB && tsA && ts > tsA) ||
      (tsA && tsB && ts > Math.min(tsA, tsB)),
    );
  },
}));
